import React, { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useParams, useLocation } from "react-router-dom";
import { I18nProvider, useI18n } from "@/i18n/I18nContext";
import ScrollToTop from "@/components/ScrollToTop";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import CookieBanner from "@/components/CookieBanner";
import Home from "@/pages/Home";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import CookiePolicy from "@/pages/CookiePolicy";
import Terms from "@/pages/Terms";
import ThankYou from "@/pages/ThankYou";
import { PATHS } from "@/lib/paths";

function DocumentMeta() {
  const { lang, t } = useI18n();
  const location = useLocation();
  useEffect(() => {
    document.documentElement.lang = lang;
    document.title = t("meta.title");
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute("content", t("meta.description"));
  }, [lang, location.pathname, t]);
  return null;
}

function LocalizedLayout({ children }) {
  return (
    <>
      <DocumentMeta />
      <Header />
      {children}
      <Footer />
      <CookieBanner />
    </>
  );
}

function LangGate({ lang, children }) {
  return (
    <I18nProvider lang={lang}>
      <LocalizedLayout>{children}</LocalizedLayout>
    </I18nProvider>
  );
}

function LangRoute() {
  const { lang } = useParams();
  if (lang !== "fr" && lang !== "en") {
    return <Navigate to="/fr" replace />;
  }
  return (
    <LangGate lang={lang}>
      <Home />
    </LangGate>
  );
}

function App() {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <Routes>
        {/* Root redirects to /fr (French is default) */}
        <Route path="/" element={<Navigate to="/fr" replace />} />

        {/* Home pages */}
        <Route path="/fr" element={<LangGate lang="fr"><Home /></LangGate>} />
        <Route path="/en" element={<LangGate lang="en"><Home /></LangGate>} />

        {/* French legal pages */}
        <Route
          path={PATHS.privacy.fr}
          element={<LangGate lang="fr"><PrivacyPolicy /></LangGate>}
        />
        <Route
          path={PATHS.cookies.fr}
          element={<LangGate lang="fr"><CookiePolicy /></LangGate>}
        />
        <Route
          path={PATHS.terms.fr}
          element={<LangGate lang="fr"><Terms /></LangGate>}
        />

        {/* English legal pages */}
        <Route
          path={PATHS.privacy.en}
          element={<LangGate lang="en"><PrivacyPolicy /></LangGate>}
        />
        <Route
          path={PATHS.cookies.en}
          element={<LangGate lang="en"><CookiePolicy /></LangGate>}
        />
        <Route
          path={PATHS.terms.en}
          element={<LangGate lang="en"><Terms /></LangGate>}
        />

        {/* Thank You: French only, mounted at /thank_you (no layout header/footer i18n) */}
        <Route path="/thank_you" element={<ThankYou />} />

        {/* Any other /:lang path — fallback to home */}
        <Route path="/:lang/*" element={<LangRoute />} />

        {/* 404 → default lang home */}
        <Route path="*" element={<Navigate to="/fr" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
